import { Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import InfoSection from "@quest_editor/components/InfoSection";
import QParamList from "@quest_editor/components/QParamList";

export type SurveyFormValues = {
  preInstruction: string;
  qParam1: string;
  qParam2: string;
  qParam3: string;
};

const SurveySettingsSection = ({
  formValues,
  onFormChange,
  onSaveAndGotoSurveyEdit,
  isSubmitting,
}: {
  formValues: SurveyFormValues;
  onFormChange: (key: keyof SurveyFormValues, value: string) => void;
  onSaveAndGotoSurveyEdit: () => void;
  isSubmitting: boolean;
}) => {
  const { t } = useTranslation("questEditor");

  return (
    <InfoSection
      title={t("quest_edit_survey.section.survey_settings")}
      rows={[
        {
          label: t("quest_edit_survey.field.pre_instruction.name"),
          children: (
            <Stack direction="column" spacing={1}>
              <TextField
                size="small"
                value={formValues.preInstruction}
                onChange={(e) =>
                  onFormChange("preInstruction", e.target.value)
                }
                sx={{ minWidth: 400 }}
              />
              <Typography color="primary">
                {t("quest_edit_survey.field.pre_instruction.note")}
              </Typography>
            </Stack>
          ),
        },
        {
          label: t("quest_edit_survey.field.qparam.name"),
          extra: (
            <Typography color="error" sx={{ whiteSpace: "pre-wrap" }}>
              {t("quest_edit_survey.field.qparam.warning")}
            </Typography>
          ),
          children: (
            <Stack direction="column" spacing={2}>
              <QParamList
                childrenList={{
                  qParam1: (
                    <TextField
                      size="small"
                      value={formValues.qParam1}
                      onChange={(e) =>
                        onFormChange("qParam1", e.target.value)
                      }
                      sx={{ minWidth: 300 }}
                    />
                  ),
                  qParam2: (
                    <TextField
                      size="small"
                      value={formValues.qParam2}
                      onChange={(e) =>
                        onFormChange("qParam2", e.target.value)
                      }
                      sx={{ minWidth: 300 }}
                    />
                  ),
                  qParam3: (
                    <TextField
                      size="small"
                      value={formValues.qParam3}
                      onChange={(e) =>
                        onFormChange("qParam3", e.target.value)
                      }
                      sx={{ minWidth: 300 }}
                    />
                  ),
                }}
              />
            </Stack>
          ),
        },
        {
          label: t("quest_edit_survey.field.survey_content.name"),
          extra: (
            <Typography color="error">
              {t("quest_edit_survey.field.survey_content.warning")}
            </Typography>
          ),
          required: true,
          children: (
            <ActionButton
              size="small"
              onClick={onSaveAndGotoSurveyEdit}
              disabled={isSubmitting}
            >
              {t("quest_edit_survey.button.save_and_goto_survey_edit")}
            </ActionButton>
          ),
        },
      ]}
    />
  );
};

export default SurveySettingsSection;
